import { Box, Typography, useTheme } from "@mui/material";
import { CHART_PADDING_TOP_COEFICIENT } from "Charts/Utils/CommonChartUtils";
import { generatePropertyPurchaseChartData, getPropertyChartAnnotations } from "Charts/Utils/PropertyPurchaseChartUtils";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getYearsInflected } from "Infrastructure/Utils/LanguageUtils";
import { Chart } from "chart.js";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { addMonths, addYears, differenceInMonths, differenceInYears, format } from "date-fns";
import type React from "react";
import { Line } from "react-chartjs-2";

type Props = {
  readonly investCurrentSavings: boolean;
  readonly currentSavingsAmount: number;
  readonly downpaymentAmount: number;
  readonly inflationRate: number;
  readonly interestRate: number;
  readonly loanAmount: number;
  readonly loanDuration: number;
  readonly loanInterestRate: number;
  readonly monthlyContributionAmount: number;
  readonly targetDate: Date;
  readonly referenceStartDate: Date;
};

Chart.register(AnnotationPlugin);

const getLabels = (savingPhaseDurationInMonths: number, repaymentPhaseDurationInYears: number, referenceStartDate: Date) => {
  const today = referenceStartDate;
  const savingPhaseLabels = new Array(savingPhaseDurationInMonths + 1)
    .fill("")
    .map((_, index) => {
      return format(addMonths(today, index), "yyyy-MM-dd");
    })
    .filter((_, index) => {
      if (savingPhaseDurationInMonths > 30) return index % 6 === 0;
      return true;
    });

  const repaymentPhaseLabels = new Array(repaymentPhaseDurationInYears + 1).fill("").map((_, index) => {
    return format(addMonths(addYears(today, index), savingPhaseDurationInMonths), "yyyy-MM-dd");
  });

  return [...savingPhaseLabels, ...repaymentPhaseLabels];
};

export const PropertyPurchaseChart: React.FC<Props> = ({
  interestRate,
  inflationRate,
  monthlyContributionAmount,
  targetDate,
  loanAmount,
  currentSavingsAmount,
  downpaymentAmount,
  loanDuration,
  loanInterestRate,
  investCurrentSavings,
  referenceStartDate,
}) => {
  const theme = useTheme();
  const { t } = useResource();

  const durationInMonths = differenceInMonths(targetDate, referenceStartDate);
  const data = generatePropertyPurchaseChartData({
    accumulationPhaseDuration: durationInMonths,
    interestRate,
    monthlyContributionAmount,
    inflationRate,
    loanAmount,
    currentSavingsAmount,
    loanDuration,
    loanInterestRate,
    investCurrentSavings,
  });

  const getDatasets = () => {
    const paddedTop = loanAmount * CHART_PADDING_TOP_COEFICIENT;
    return [
      {
        data: [paddedTop],
        borderColor: "transparent",
        backgroundColor: "transparent",
        order: 0,
      },
      {
        data: data.accumulationPhaseData,
        fill: true,
        pointStyle: "dash",
        borderColor: "transparent",
        backgroundColor: theme.colors.chartPositive,
        order: 1,
        tension: 0,
      },
      {
        data: data.repaymentPhaseData,
        fill: true,
        pointStyle: "dash",
        borderColor: "transparent",
        order: 2,
        backgroundColor: theme.colors.chartNeutral,
        tension: 0,
      },
    ];
  };

  const options = {
    locale: "en-US",
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context: any) => {
          let tooltipEl = document.getElementById("chartjs-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<table></table>";
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }

          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }

          function getBody(bodyItem: any) {
            return bodyItem.lines;
          }

          if (tooltipModel.body) {
            const bodyLines = tooltipModel.body.map(getBody);
            let innerHtml = "<thead>";

            if (tooltipModel.dataPoints[0].dataIndex < data.accumulationPhaseData.length) {
              const accumulationPhaseYears = differenceInYears(targetDate, new Date());
              innerHtml += `</thead><tbody><tr><td style='font-weight: 500;'>
                    ${t(Resources.AnalysisDetail.Goals.InvestmentDuration)}: ${accumulationPhaseYears} ${t(getYearsInflected(accumulationPhaseYears))}
                    </td></tr>`;
            } else {
              innerHtml += `</thead><tbody><tr><td style='font-weight: 500;'>
                    ${t(Resources.AnalysisDetail.Goals.LoanPaymentAct)}: ${loanDuration} ${t(getYearsInflected(loanDuration))}
                    </td></tr>`;
            }

            const date = format(new Date(tooltipModel.dataPoints[0].label), "M/yyyy");
            const amount = `<span>${formatCurrency(Number(bodyLines[0][0].toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>`;

            innerHtml += `<tr><td>${date}</td></tr><tr><td>${amount}</td></tr>`;
            innerHtml += "</tbody>";

            const tableRoot = tooltipEl.querySelector("table");

            if (tableRoot) {
              tableRoot.innerHTML = innerHtml;
            }
          }

          const chartPosition = context.chart.canvas.getBoundingClientRect();
          const tooltipPosition = tooltipEl.getBoundingClientRect();

          const width = tooltipPosition.right - tooltipPosition.left;
          const height = tooltipPosition.bottom - tooltipPosition.top;

          tooltipEl.style.opacity = "1";
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left = `${chartPosition.left + window.pageXOffset + tooltipModel.caretX - width}px`;
          tooltipEl.style.top = `${chartPosition.top + window.pageYOffset + tooltipModel.caretY - height}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = "none";
          tooltipEl.style.zIndex = "10000";

          tooltipEl.style.fontFamily = "Roboto";
          tooltipEl.style.fontSize = "10px";
          tooltipEl.style.lineHeight = "1.1";
          tooltipEl.style.borderWidth = "1px";
          tooltipEl.style.borderStyle = "solid";
          tooltipEl.style.borderColor = theme.colors.borderLight;
          tooltipEl.style.backgroundColor = theme.colors.white;
          tooltipEl.style.borderRadius = theme.spacing(Radius.M);
          tooltipEl.style.padding = theme.spacing(Spacing.M);
        },
      },
      annotation: {
        annotations: {
          ...getPropertyChartAnnotations({
            theme,
            loanAmount,
            totalDuration: data.repaymentPhaseData.length,
            accumulationPhaseDuration: data.accumulationPhaseData.length,
            downpaymentAmount,
          }),
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          font: {
            color: "black",
            weight: 600,
            size: 10,
            family: "Roboto",
            lineHeight: 1.3,
          },
          callback: (value: string | number, index: number) => {
            const date = referenceStartDate;
            const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

            // first date
            if (index === 0) {
              return format(addMonths(firstDayOfMonth, Number(value)), "yyyy");
            }

            if (index === data.accumulationPhaseData.length) {
              return format(targetDate, "yyyy");
            }

            if (index === data.repaymentPhaseData.length - 1) {
              return format(addYears(targetDate, loanDuration), "yyyy");
            }

            if (index > data.repaymentPhaseData.length) {
              return "";
            }
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        suggestedMin: 0,
        beginAtZero: true,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
    layout: {
      padding: { right: 32 },
    },
  };

  return (
    <Box>
      <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary">
        {t(Resources.Common.Value)}
      </Typography>
      <Box display="flex" alignItems="end">
        <Line
          options={options}
          data={{
            labels: getLabels(durationInMonths, loanDuration, referenceStartDate),
            datasets: getDatasets(),
          }}
        />
        <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary" mb={5.2} ml={-5}>
          {t(Resources.Common.Time)}
        </Typography>
      </Box>
    </Box>
  );
};
